import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('de'); // Default: Almanca
  const [translations, setTranslations] = useState({});

  // Supported languages
  const supportedLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' }
  ];

  // Basit translation fonksiyonu
  const t = (key, options = {}) => {
    const { params = {}, defaultValue } = options;
    let translation = translations[key];
    
    if (!translation) {
      return defaultValue !== undefined ? defaultValue : key;
    }
    
    // Parametreleri değiştir
    return translation.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  };

  // Dil değiştirme fonksiyonu
  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    localStorage.setItem('language', languageCode);
    // Yeni dil için translation'ları yükle
    loadTranslations(languageCode);
  };

  // Component mount olduğunda - sadece bir kez
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'de';
    setCurrentLanguage(savedLanguage);
    
    // Backend'den translation'ları çek
    loadTranslations(savedLanguage);
  }, []); // Boş dependency array - sadece bir kez çalışır

  // Translation'ları backend'den yükle ve localStorage'a kaydet
  const loadTranslations = async (languageCode) => {
    try {
      // Önce localStorage'dan kontrol et
      const cachedTranslations = localStorage.getItem(`translations_${languageCode}`);
      if (cachedTranslations) {
        console.log(`Translation cache hit for ${languageCode}`);
        setTranslations(JSON.parse(cachedTranslations));
        return;
      }

      console.log(`Translation cache miss for ${languageCode}, fetching from backend...`);
      
      // Backend'den çek
      const { data } = await api.get(`/api/language/${languageCode}`);
      if (data && data.success !== false) {
        const translationData = data.data.translations;
        
        // localStorage'a kaydet
        localStorage.setItem(`translations_${languageCode}`, JSON.stringify(translationData));
        console.log(`Saved ${Object.keys(translationData).length} translations to cache for ${languageCode}`);
        setTranslations(translationData);
      } else {
        console.warn('Backend response not ok, using fallback');
        // Fallback: Basit default translations
        loadDefaultTranslations();
      }
    } catch (error) {
      console.warn('Translation yüklenemedi, default kullanılıyor:', error);
      // Fallback: Basit default translations
      loadDefaultTranslations();
    }
  };

  // Translation cache'ini temizle
  const clearTranslationCache = () => {
    localStorage.removeItem(`translations_${currentLanguage}`);
    // Yeni translation'ları yükle
    loadTranslations(currentLanguage);
  };

  // Tüm dil cache'lerini temizle (EN ve DE)
  const clearAllTranslationCache = () => {
    localStorage.removeItem('translations_en');
    localStorage.removeItem('translations_de');
    // Mevcut dil için yeni translation'ları yükle
    loadTranslations(currentLanguage);
  };

  // Basit default translations (fallback)
  const loadDefaultTranslations = () => {
    const defaultTranslations = {
      'nav.dashboard': 'Dashboard',
      'nav.proposals': 'Proposals',
      'nav.inventory': 'Inventory',
      'nav.invoices': 'Invoices',
      'nav.users': 'Users',
      'nav.settings': 'Settings',
      'nav.profile': 'Profile',
      'nav.logout': 'Logout',
      'nav.suppliers': 'Suppliers',
      'nav.clients': 'Clients',
      'common.search': 'Search',
      'common.new': 'New',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.save': 'Save',
      'common.cancel': 'Cancel',
      'common.close': 'Close',
      'common.view': 'View',
      'common.actions': 'Actions',
      'common.name': 'Name',
      'common.email': 'Email',
      'common.company': 'Company',
      'common.status': 'Status',
      'common.date': 'Date',
      'common.time': 'Time',
      'common.phone': 'Phone',
      'common.address': 'Address',
      'common.category': 'Category',
      'common.rating': 'Rating',
      'common.notes': 'Notes',
      'clients.title': 'Clients',
      'suppliers.title': 'Suppliers',
      'settings.language.select': 'Select Language',
      // Proposals için ek translation'lar
      'proposals.title': 'Proposals',
      'proposals.status': 'Status',
      'common.created_at': 'Created At',
      'common.desc': 'Descending'
    };
    
    setTranslations(defaultTranslations);
  };

  const value = {
    currentLanguage,
    translations,
    supportedLanguages,
    t,
    changeLanguage,
    clearTranslationCache,
    clearAllTranslationCache
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};


